import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { adminListOrders, adminUpdateOrder, adminDeleteOrder } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const STATUS = ['pending', 'payment_submitted', 'paid', 'shipped', 'delivered', 'cancelled'];
const STATUS_STYLE = {
  pending: 'bg-yellow-100 text-yellow-800',
  payment_submitted: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  shipped: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListOrders();
      setOrders(data);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load orders.' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateOrder(id, { status });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      toast({ title: 'Updated', description: `Order #${id} → ${status}` });
    } catch { toast({ title: 'Error', description: 'Failed to update.' }); }
  };

  const removeOrder = async (id) => {
    if (!window.confirm(`Delete order #${id}?`)) return;
    try {
      await adminDeleteOrder(id);
      setOrders(prev => prev.filter(o => o.id !== id));
    } catch { toast({ title: 'Error', description: 'Failed to delete.' }); }
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl">Orders</h1>
          <p className="text-sm text-neutral-500">{orders.length} orders total</p>
        </div>
        <Button onClick={load} variant="outline" className="gap-2"><RefreshCw size={14}/> Refresh</Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-neutral-500"><Loader2 className="animate-spin" size={16}/> Loading orders...</div>
      ) : orders.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">No orders yet.</div>
      ) : (
        <div className="bg-white border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left p-3">Order</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Total</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Placed</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <React.Fragment key={o.id}>
                    <tr className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="p-3 font-mono font-semibold">#{o.id}</td>
                      <td className="p-3">
                        <div className="font-medium">{o.name}</div>
                        <div className="text-xs text-neutral-500">{o.phone}</div>
                      </td>
                      <td className="p-3 text-[#b8892d] font-semibold">₹{Number(o.total).toLocaleString('en-IN')}</td>
                      <td className="p-3">
                        <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)} className={`px-2 py-1 rounded text-xs border-0 font-medium ${STATUS_STYLE[o.status] || 'bg-neutral-100'}`}>
                          {STATUS.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
                        </select>
                      </td>
                      <td className="p-3 text-xs text-neutral-500">{o.created_at ? new Date(o.created_at).toLocaleString('en-IN') : '-'}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="text-neutral-500 hover:text-[#b8892d] p-1">
                          {expanded === o.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                        </button>
                        <button onClick={() => removeOrder(o.id)} className="text-neutral-400 hover:text-red-600 p-1 ml-1"><Trash2 size={14}/></button>
                      </td>
                    </tr>
                    {expanded === o.id && (
                      <tr className="bg-neutral-50">
                        <td colSpan={6} className="p-5">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold mb-2 text-sm">Delivery Details</h4>
                              <div className="text-sm text-neutral-700 space-y-0.5">
                                <div>{o.name}</div>
                                <div>{o.email}</div>
                                <div>{o.phone}</div>
                                <div>{o.address}</div>
                                <div>{o.city} {o.pincode}</div>
                                {o.notes && <div className="pt-2 text-xs italic text-neutral-500">Notes: {o.notes}</div>}
                              </div>
                              {o.transaction_id && (
                                <div className="mt-4 p-3 border border-green-200 bg-green-50 text-sm">
                                  <div className="font-semibold text-green-800 text-xs uppercase mb-1">Payment Info</div>
                                  <div>UTR / Txn ID: <span className="font-mono">{o.transaction_id}</span></div>
                                  {o.payer_name && <div>Payer: {o.payer_name}</div>}
                                  {o.payment_screenshot && (
                                    <div className="mt-2">
                                      <div className="text-xs text-neutral-600 mb-1">Payment Screenshot:</div>
                                      <a href={o.payment_screenshot} target="_blank" rel="noreferrer">
                                        <img src={o.payment_screenshot} alt="Payment proof" className="w-40 border border-neutral-200 hover:border-[#b8892d]"/>
                                      </a>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div>
                              <h4 className="font-semibold mb-2 text-sm">Items ({o.items?.length || 0})</h4>
                              <div className="space-y-2">
                                {o.items?.map((it, i) => (
                                  <div key={i} className="flex gap-2 items-center bg-white p-2 border border-neutral-200">
                                    <img src={it.image} alt={it.title} className="w-12 h-12 object-cover"/>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-xs line-clamp-1">{it.title}</div>
                                      <div className="text-xs text-neutral-500">Qty {it.qty} · ₹{Number(it.sale).toLocaleString('en-IN')}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
